import { useState } from 'react';
import { SmartAnalytics } from '@/components/dashboard/analytics/SmartAnalytics';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/dashboard/shared/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SalesChart } from '@/components/dashboard/analytics/SalesChart';
import { StatsGrid } from '@/components/dashboard/analytics/StatsGrid';
import { KpiGrid } from '@/components/dashboard/shared/KpiGrid';
import { SectionCard } from '@/components/dashboard/shared/SectionCard';
import { dashboardStyles } from '@/components/dashboard/shared/dashboard-styles';
import {
  AlertCircle,
} from 'lucide-react';
import {
  RiShoppingCartLine,
  RiArchiveLine,
  RiAddLine,
  RiArrowRightLine,
  RiLineChartLine,
  RiCheckLine,
  RiLockLine,
  RiBarChartGroupedLine,
  RiDownloadLine,
  RiFileTextLine,
  RiSparkling2Line,
} from '@remixicon/react';
import api from '@/utils/api';
import { isAxiosError } from 'axios';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { DashboardHead } from '@/components/SEO/DashboardHead';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useSettings } from '@/contexts/SettingsContext';
import { Progress } from '@/components/ui/progress';
import { statusBadgeClass } from "@/lib/status";

const timeRanges = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'This month', value: 'month' },
  { label: 'This year', value: 'year' },
  { label: 'All time', value: 'all' },
];

const containerClass = 'mx-auto space-y-6 relative';

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('30d');
  const [mode, setMode] = useState<'standard' | 'smart'>('standard');
  const { currentStore, storeId } = useCurrentStore();
  const { bookingSettings } = useSettings();

  const { data: analytics, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', timeRange, storeId],
    queryFn: async () => {
      const now = new Date();
      const startDate = new Date(now);
      if (timeRange === '7d') startDate.setDate(now.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(now.getDate() - 30);
      else if (timeRange === 'month') startDate.setMonth(now.getMonth() - 1);
      else if (timeRange === 'year') startDate.setFullYear(now.getFullYear() - 1);
      else if (timeRange === 'all') startDate.setTime(0);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: now.toISOString(),
      });

      const { data } = await api.get(`/api/analytics?${params.toString()}`, {
        headers: { 'Store-ID': storeId },
      });
      return data;
    },
    enabled: !!storeId,
    retry: (failureCount, err) => {
      if (isAxiosError(err) && err.response?.status === 403) return false;
      return failureCount < 2;
    },
    retryDelay: 1000,
  });

  const planRestricted =
    isAxiosError(error) &&
    error.response?.status === 403 &&
    (error.response?.data as { code?: string })?.code === 'PLAN_RESTRICTED';

  const handleExport = async () => {
    try {
      const now = new Date();
      const startDate = new Date(now);
      if (timeRange === '7d') startDate.setDate(now.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(now.getDate() - 30);
      else if (timeRange === 'month') startDate.setMonth(now.getMonth() - 1);
      else if (timeRange === 'year') startDate.setFullYear(now.getFullYear() - 1);
      else if (timeRange === 'all') startDate.setTime(0);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: now.toISOString(),
      });

      const { data } = await api.get(`/api/analytics/export?${params.toString()}`, {
        headers: { 'Store-ID': storeId },
        responseType: 'blob',
      });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${timeRange}-${new Date().toISOString()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currentStore?.currency || 'NGN',
    }).format(amount);
  };

  const getStatusColor = statusBadgeClass;

  const bookingSummary = analytics?.bookingSummary;
  const invoiceSummary = analytics?.invoiceSummary;
  const totalBookings = bookingSummary?.totalBookings || analytics?.summary?.totalBookings || 0;
  const bookingRevenue =
    bookingSummary?.totalRevenue ?? analytics?.summary?.bookingRevenue ?? 0;
  const totalRevenue = analytics?.summary?.totalRevenue || 0;
  const averageBookingValue = totalBookings > 0 ? bookingRevenue / totalBookings : 0;
  const bookingRevenueShare = totalRevenue > 0 ? (bookingRevenue / totalRevenue) * 100 : 0;

  const totalInvoices = invoiceSummary?.totalInvoices || analytics?.summary?.totalInvoices || 0;
  const invoiceRevenue =
    invoiceSummary?.totalRevenue ?? analytics?.summary?.invoiceRevenue ?? 0;
  const invoiceOutstanding =
    invoiceSummary?.outstandingAmount ?? analytics?.summary?.invoiceOutstanding ?? 0;
  const paidInvoices = invoiceSummary?.paidInvoices || 0;
  const averageInvoiceValue = totalInvoices > 0 ? invoiceRevenue / totalInvoices : 0;
  const combinedRevenue = totalRevenue + bookingRevenue + invoiceRevenue;
  const invoiceRevenueShare = combinedRevenue > 0 ? (invoiceRevenue / combinedRevenue) * 100 : 0;

  const pageActions = (
    <>
      <div className="flex rounded-full bg-[#F5F5F3] p-0.5" role="tablist" aria-label="Analytics view">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'standard'}
          onClick={() => setMode('standard')}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${mode === 'standard' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground'}`}
        >
          Standard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'smart'}
          onClick={() => setMode('smart')}
          className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-semibold ${mode === 'smart' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'}`}
        >
          <RiSparkling2Line className="size-3.5" />
          Smart
        </button>
      </div>
      <Select value={timeRange} onValueChange={setTimeRange}>
        <SelectTrigger className={`${dashboardStyles.pillTrigger} w-[180px]`}>
          <SelectValue placeholder="Select time range" />
        </SelectTrigger>
        <SelectContent>
          {timeRanges.map((range) => (
            <SelectItem key={range.value} value={range.value}>
              {range.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {mode === 'standard' && (
        <Button onClick={handleExport} variant="outline" size="sm" className="h-auto gap-1 rounded-full px-4 text-sm font-semibold">
          <RiDownloadLine className="h-4 w-4" />
          Export
        </Button>
      )}
    </>
  );

  if (!storeId) {
    return (
      <div className={containerClass}>
        <DashboardHead pageName="Analytics" />
        <PageHeader
          title="Analytics"
          subtitle="Select a store to view analytics"
        />
        <SectionCard>
          <div className="text-center text-muted-foreground">
            No store selected.
          </div>
        </SectionCard>
      </div>
    );
  }

  if (planRestricted) {
    const premiumFeatures = [
      { icon: RiLineChartLine, label: 'Sales trends & revenue charts' },
      { icon: RiBarChartGroupedLine, label: 'Smart mode for sales-link visitors' },
      { icon: RiBarChartGroupedLine, label: 'Top performing products' },
      { icon: RiShoppingCartLine, label: 'Order analytics & history' },
      { icon: RiFileTextLine, label: 'Invoice analytics & payment tracking' },
      { icon: RiDownloadLine, label: 'CSV data export' },
    ];

    return (
      <div className={containerClass}>
        <DashboardHead pageName="Analytics" />
        <PageHeader
          title="Analytics"
          subtitle="Track your store performance and insights"
        />

        <Card className="overflow-hidden border-none shadow-none bg-gradient-to-br from-primary/5 via-transparent to-primary/8">
          <div className="relative p-6 md:p-10 flex flex-col md:flex-row gap-10 items-start ">
            {/* Left, copy & features */}
            <div className="flex-1 space-y-6">
              {/* Badge + heading */}
              <div className="space-y-3">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  <RiLockLine className="h-3 w-3" />
                  Premium feature
                </div>
                <h2 className="text-xl font-semibold leading-snug">
                  Unlock powerful analytics<br className="hidden sm:block" /> for your store
                </h2>
                <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                  Understand what's driving your revenue. Premium gives you deep
                  insights into sales, products, bookings, and more, all in one
                  place.
                </p>
              </div>

              {/* Feature list */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                {premiumFeatures.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2.5 rounded-2xl border border-primary/5 bg-[#f6f4ef] px-3 py-2.5">
                    <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-sm text-foreground/80">{label}</span>
                    <RiCheckLine className="h-3.5 w-3.5 text-primary ml-auto shrink-0" />
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Button asChild size="lg" className="gap-2">
                <Link to="/dashboard/settings#billing">
                  Upgrade to Premium
                  <RiArrowRightLine className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Right, blurred preview */}
            <div className="hidden md:flex flex-col gap-3 w-64 shrink-0 select-none pointer-events-none">
              <div className="blur-[3px] opacity-50 space-y-3">
                {/* Fake stats row */}
                <div className="grid grid-cols-2 gap-2">
                  {['Total Revenue', 'Total Orders', 'Growth Rate', 'Avg. Order'].map((label) => (
                    <div key={label} className="rounded-xl border bg-muted/60 p-3 space-y-1.5">
                      <div className="h-2 w-16 rounded bg-muted-foreground/20" />
                      <div className="h-4 w-20 rounded bg-muted-foreground/30" />
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                {/* Fake chart bars */}
                <div className="rounded-xl border bg-muted/60 p-3">
                  <div className="h-2 w-24 rounded bg-muted-foreground/20 mb-3" />
                  <div className="flex items-end gap-1 h-20">
                    {[40, 65, 50, 80, 55, 90, 70].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-primary/30"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
                {/* Fake list rows */}
                <div className="rounded-xl border bg-muted/60 p-3 space-y-2">
                  {[80, 60, 45].map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded bg-muted-foreground/20 shrink-0" />
                      <div className="h-2 rounded bg-muted-foreground/20 flex-1" style={{ maxWidth: `${w}%` }} />
                      <div className="h-2 w-10 rounded bg-muted-foreground/20 ml-auto" />
                    </div>
                  ))}
                </div>
              </div>
             
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (mode === 'smart' && storeId) {
    return (
      <div className={containerClass}>
        <DashboardHead pageName="Analytics" />
        <PageHeader
          title="Analytics"
          subtitle="A read of your sales link: visitors, devices, locations, and where people leave"
          actions={pageActions}
        />
        <SmartAnalytics timeRange={timeRange} storeId={storeId} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <DashboardHead pageName="Analytics" />
        <PageHeader
          title="Analytics"
          subtitle="Track your store performance and insights"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load analytics data. Please try again.
            <Button
              variant="link"
              className="p-0 h-auto font-normal underline ml-2"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <DashboardHead pageName="Analytics" />
      <PageHeader
        title="Analytics"
        subtitle="Track your store performance and insights"
        actions={pageActions}
      />

      {/* Stats Grid */}
      <KpiGrid loading={isLoading} skeletonCount={4}>
        {!isLoading && <StatsGrid stats={analytics?.summary} />}
      </KpiGrid>

      {/* Booking Analytics */}
      {bookingSettings?.enabled && (
        <SectionCard className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium text-primary uppercase tracking-wide mb-1">
                Booking Performance
              </p>
              <p className="text-lg font-semibold">
                How bookings contribute to your revenue
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on bookings created in this period. Only completed payments are counted as booking revenue.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 text-right">
              <span className="text-xs text-muted-foreground">Bookings share of revenue</span>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-semibold">
                  {bookingRevenueShare.toFixed(0)}%
                </span>
                <span className="text-xs text-muted-foreground">of total</span>
              </div>
              <div className="w-40">
                <Progress value={bookingRevenueShare} max={100} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`${dashboardStyles.statTile}`}>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Total bookings
              </p>
              <p className="text-2xl font-semibold font-heading">
                {totalBookings}
              </p>
              <p className="text-xs text-muted-foreground">
                For the selected date range
              </p>
            </div>

            <div className={`${dashboardStyles.statTile}`}>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Booking revenue
              </p>
              <p className="text-2xl font-semibold font-heading">
                {formatCurrency(bookingRevenue)}
              </p>
              <p className="text-xs text-muted-foreground">
                Only completed payments are included
              </p>
            </div>

            <div className={`${dashboardStyles.statTile}`}>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Avg. booking value
              </p>
              <p className="text-2xl font-semibold font-heading">
                {formatCurrency(averageBookingValue || 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                {totalBookings > 0 ? `Across ${totalBookings} bookings` : 'No bookings in this period'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">Status breakdown</p>
              <p className="text-xs text-muted-foreground">
                See how your bookings are distributed across statuses
              </p>
            </div>

            {totalBookings > 0 && bookingSummary?.statusBreakdown && Object.keys(bookingSummary.statusBreakdown).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(bookingSummary.statusBreakdown).map(([status, count]) => {
                  const value = count as number;
                  const percentage = totalBookings > 0 ? (value / totalBookings) * 100 : 0;
                  return (
                    <div
                      key={status}
                      className="flex items-center gap-3 rounded-2xl border border-primary/5 bg-[#f6f4ef] px-3 py-2.5"
                    >
                      <div className="min-w-[80px]">
                        <Badge className="capitalize" variant="outline">
                          {status}
                        </Badge>
                      </div>
                      <div className="flex-1 space-y-1">
                        <Progress value={percentage} max={100} />
                        <p className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{value} bookings</span>
                          <span>{percentage.toFixed(0)}%</span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-primary/15 bg-[#f6f4ef] px-4 py-6 text-center text-sm font-medium text-muted-foreground">
                No booking activity recorded for this period yet. Once customers start booking,
                you'll see a breakdown of each status here.
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Invoice Analytics */}
      <SectionCard className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-primary uppercase tracking-wide mb-1">
              Invoice Performance
            </p>
            <p className="text-lg font-semibold">
              Track billed revenue and outstanding balances
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on invoices created in this period. Collected revenue reflects payments recorded on those invoices.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 text-right">
            <span className="text-xs text-muted-foreground">Invoices share of revenue</span>
            <div className="flex items-baseline gap-1">
              <span className="text-xl font-semibold">
                {invoiceRevenueShare.toFixed(0)}%
              </span>
              <span className="text-xs text-muted-foreground">of combined</span>
            </div>
            <div className="w-40">
              <Progress value={invoiceRevenueShare} max={100} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={dashboardStyles.statTile}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Total invoices
            </p>
            <p className="text-2xl font-semibold font-heading">
              {totalInvoices}
            </p>
            <p className="text-xs text-muted-foreground">
              Created in this period
            </p>
          </div>

          <div className={dashboardStyles.statTile}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Revenue collected
            </p>
            <p className="text-2xl font-semibold font-heading">
              {formatCurrency(invoiceRevenue)}
            </p>
            <p className="text-xs text-muted-foreground">
              Payments recorded on invoices
            </p>
          </div>

          <div className={dashboardStyles.statTile}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Outstanding
            </p>
            <p className="text-2xl font-semibold font-heading text-amber-700">
              {formatCurrency(invoiceOutstanding)}
            </p>
            <p className="text-xs text-muted-foreground">
              Unpaid balance on open invoices
            </p>
          </div>

          <div className={dashboardStyles.statTile}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Paid invoices
            </p>
            <p className="text-2xl font-semibold font-heading">
              {paidInvoices}
            </p>
            <p className="text-xs text-muted-foreground">
              {totalInvoices > 0
                ? `Avg. collected ${formatCurrency(averageInvoiceValue)}`
                : 'No invoices in this period'}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold">Status breakdown</p>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs font-normal text-muted-foreground h-7 px-2"
            >
              <Link to="/dashboard/invoices">
                View all
                <RiArrowRightLine className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>

          {totalInvoices > 0 && invoiceSummary?.statusBreakdown && Object.keys(invoiceSummary.statusBreakdown).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(invoiceSummary.statusBreakdown).map(([status, count]) => {
                const value = count as number;
                const percentage = totalInvoices > 0 ? (value / totalInvoices) * 100 : 0;
                return (
                  <div
                    key={status}
                    className="flex items-center gap-3 rounded-2xl border border-primary/5 bg-[#f6f4ef] px-3 py-2.5"
                  >
                    <div className="min-w-[80px]">
                      <Badge className={`capitalize ${getStatusColor(status)}`}>
                        {status}
                      </Badge>
                    </div>
                    <div className="flex-1 space-y-1">
                      <Progress value={percentage} max={100} />
                      <p className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{value} invoices</span>
                        <span>{percentage.toFixed(0)}%</span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-primary/15 bg-[#f6f4ef] px-4 py-6 text-center text-sm font-medium text-muted-foreground">
              No invoices created in this period yet.{' '}
              <Link to="/dashboard/invoices/new" className="text-primary underline underline-offset-2">
                Create your first invoice
              </Link>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Chart */}
        <SectionCard>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold">Revenue Over Time</p>
              <p className="text-xs text-muted-foreground">
                {timeRanges.find((r) => r.value === timeRange)?.label}
              </p>
            </div>
            {!isLoading && analytics?.summary?.growthRate !== undefined && (
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <RiLineChartLine className="h-4 w-4 text-emerald-500" />
                <span className="text-emerald-600">
                  +{analytics.summary.growthRate}%
                </span>
                <span className="text-xs text-muted-foreground font-normal">vs prev</span>
              </div>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-[250px] w-full rounded-xl" />
          ) : (
            <SalesChart data={analytics?.salesData ?? []} />
          )}
        </SectionCard>

        {/* Top Products */}
        <SectionCard variant="list">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Top Performing Products</p>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs font-normal text-muted-foreground h-7 px-2"
            >
              <Link to="/dashboard/products">
                View All
                <RiArrowRightLine className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>

          <div className="space-y-1.5">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-transparent"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                    <div>
                      <Skeleton className="h-3 w-32 mb-1.5" />
                      <Skeleton className="h-2.5 w-20" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-3 w-16 mb-1.5 ml-auto" />
                    <Skeleton className="h-2.5 w-12 ml-auto" />
                  </div>
                </div>
              ))
            ) : analytics?.topProducts?.length > 0 ? (
              analytics.topProducts.map((product) => (
                <div
                  key={product.id}
                  className={dashboardStyles.listItemMuted}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <RiArchiveLine className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-sm leading-tight truncate max-w-[140px]">
                        {product.name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {product.sales} sales
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm font-heading leading-tight">
                      {formatCurrency(product.revenue)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">Revenue</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center mb-3">
                  <RiArchiveLine className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium mb-1">No products yet</p>
                <p className="text-xs text-muted-foreground text-center mb-4 max-w-[180px]">
                  Add products to start tracking sales performance.
                </p>
                <Button asChild size="sm" variant="outline" className="h-7 text-xs px-3">
                  <Link to="/dashboard/products/new">
                    <RiAddLine className="h-3.5 w-3.5 mr-1.5" />
                    Add First Product
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Recent Invoices */}
      <SectionCard variant="list">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Recent Invoices</p>
          <div className="flex items-center gap-1">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs font-normal text-muted-foreground h-7 px-2"
            >
              <Link to="/dashboard/invoices/new">
                <RiAddLine className="h-3.5 w-3.5" />
                New
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs font-normal text-muted-foreground h-7 px-2"
            >
              <Link to="/dashboard/invoices">
                View All
                <RiArrowRightLine className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-transparent"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                  <div>
                    <Skeleton className="h-3 w-28 mb-1.5" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                </div>
                <div className="text-right">
                  <Skeleton className="h-3 w-16 mb-1.5 ml-auto" />
                  <Skeleton className="h-4 w-16 ml-auto rounded-full" />
                </div>
              </div>
            ))
          ) : analytics?.recentInvoices?.length > 0 ? (
            analytics.recentInvoices.map((invoice: {
              id: string;
              invoiceNumber: string;
              customer: string;
              amount: number;
              status: string;
            }) => (
              <Link
                key={invoice.id}
                to={`/dashboard/invoices/${invoice.id}`}
                className={dashboardStyles.listItemMuted}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <RiFileTextLine className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm leading-tight truncate">
                      {invoice.invoiceNumber}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {invoice.customer}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <div className="font-semibold text-sm font-heading leading-tight">
                    {formatCurrency(invoice.amount)}
                  </div>
                  <Badge className={`mt-0.5 capitalize ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </Badge>
                </div>
              </Link>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center mb-3">
                <RiFileTextLine className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No invoices yet</p>
              <p className="text-xs text-muted-foreground text-center mb-4 max-w-[180px]">
                Create invoices to track billed revenue alongside your store orders.
              </p>
              <Button asChild size="sm" variant="outline" className="h-7 text-xs px-3">
                <Link to="/dashboard/invoices/new">
                  <RiAddLine className="h-3.5 w-3.5 mr-1.5" />
                  Create Invoice
                </Link>
              </Button>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Recent Orders */}
      <SectionCard variant="list">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Recent Orders</p>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs font-normal text-muted-foreground h-7 px-2"
          >
            <Link to="/dashboard/orders">
              View All
              <RiArrowRightLine className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>

        <div className="space-y-1.5">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-transparent"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 shrink-0 rounded-xl" />
                  <div>
                    <Skeleton className="h-3 w-28 mb-1.5" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                </div>
                <div className="text-right">
                  <Skeleton className="h-3 w-16 mb-1.5 ml-auto" />
                  <Skeleton className="h-4 w-16 ml-auto rounded-full" />
                </div>
              </div>
            ))
          ) : analytics?.recentOrders?.length > 0 ? (
            analytics.recentOrders.map((order) => (
              <div
                key={order.id}
                className={dashboardStyles.listItemMuted}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <RiShoppingCartLine className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-sm leading-tight">
                      {order.orderNumber}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {order.customer}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-sm font-heading leading-tight">
                    {formatCurrency(order.amount)}
                  </div>
                  <Badge className={`mt-0.5 ${getStatusColor(order.status)}`}>
                    {order.status}
                  </Badge>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center mb-3">
                <RiShoppingCartLine className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No orders yet</p>
              <p className="text-xs text-muted-foreground text-center mb-4 max-w-[180px]">
                Share your store link to start receiving orders.
              </p>
              <Button asChild size="sm" variant="outline" className="h-7 text-xs px-3">
                <Link to="/dashboard/products/new">
                  <RiAddLine className="h-3.5 w-3.5 mr-1.5" />
                  Add First Product
                </Link>
              </Button>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
